import {
  ArrowLeft,
  Award,
  Eye,
  EyeOff,
  Lock,
  LogIn,
  Mail,
  QrCode,
  Shield,
  Sparkles,
} from "lucide-react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log("Attempting login with:", formData.email);
      const success = await login(formData.email, formData.password);
      console.log("Login success:", success);
      if (success) {
        console.log("Login successful, redirection should happen");
        // La redirection est gérée dans AuthContext
        // Fallback de redirection après 2 secondes
        setTimeout(() => {
          console.log("Fallback redirection triggered");
          window.location.href = "/app/dashboard";
        }, 2000);
      } else {
        console.log("Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Erreur lors de la connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const demoAccounts = [
    {
      role: "Super Admin",
      email: "superadmin@demo.com",
      password: "superadmin123",
      icon: "👑",
      color: "from-purple-500 to-pink-500",
    },
    {
      role: "Admin",
      email: "admin@demo.com",
      password: "admin123",
      icon: "🛡️",
      color: "from-blue-500 to-cyan-500",
    },
    {
      role: "Parent",
      email: "parent@demo.com",
      password: "demo123",
      icon: "👤",
      color: "from-green-500 to-emerald-500",
    },
  ];

  const handleDemoLogin = async (email, password) => {
    setFormData({ email, password });
    setLoading(true);

    try {
      console.log("Demo login attempt with:", email);
      const success = await login(email, password);
      console.log("Demo login success:", success);
      if (success) {
        console.log("Demo login successful, redirection should happen");
        // La redirection est gérée dans AuthContext
        // Fallback de redirection après 2 secondes
        setTimeout(() => {
          console.log("Demo fallback redirection triggered");
          window.location.href = "/app/dashboard";
        }, 2000);
      } else {
        console.log("Demo login failed");
      }
    } catch (error) {
      console.error("Demo login error:", error);
      toast.error("Erreur lors de la connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à l'accueil
          </Link>

          <div className="flex justify-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white">
              <LogIn className="h-8 w-8" />
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-2">Bienvenue !</h2>
          <p className="text-gray-600">
            Connectez-vous à votre compte e-Tampon
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Adresse email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="votre@email.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Votre mot de passe"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 transition-all duration-200"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Connexion...
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  Se connecter
                </>
              )}
            </button>
          </form>

          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-500 font-medium"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Pas encore de compte ?{" "}
              <span className="font-medium text-gray-400">
                Contactez l'administration
              </span>
            </p>
          </div>
        </div>

        {/* Demo Accounts */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex items-center mb-4">
            <Sparkles className="h-5 w-5 mr-2" />
            <h3 className="text-lg font-semibold">Comptes de démonstration</h3>
          </div>

          <div className="space-y-3">
            {demoAccounts.map((account, index) => (
              <button
                key={index}
                onClick={() => handleDemoLogin(account.email, account.password)}
                disabled={loading}
                className={`w-full flex items-center justify-between p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all duration-200 disabled:opacity-50`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{account.icon}</span>
                  <div className="text-left">
                    <p className="font-medium">{account.role}</p>
                    <p className="text-xs text-blue-100">{account.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {account.role === "Super Admin" && (
                    <Shield className="h-4 w-4" />
                  )}
                  {account.role === "Admin" && <Award className="h-4 w-4" />}
                  {account.role === "Parent" && <QrCode className="h-4 w-4" />}
                </div>
              </button>
            ))}
          </div>

          <p className="text-xs text-blue-100 mt-4 text-center">
            Cliquez sur un compte pour vous connecter automatiquement
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
